/**
 * Settings Service for managing application configuration
 */

import { getAccessToken } from '../auth/authService';

export interface AppSettings {
  sharePointEmbedded: {
    defaultGroupId: string;
    defaultGroupAlias: string;
    defaultGroupEmail: string;
  };
  roleManagement: RoleManagementSettings;
  uiFeatures: UiFeatureFlags;
}

export interface SharePointEmbeddedSettings {
  defaultGroupId: string;
  defaultGroupAlias: string;
  defaultGroupEmail: string;
}

export interface RoleManagementSettings {
  adminGroup: string;
  userGroup: string;
}

export interface UiFeatureFlags {
  dashboardShowReportCards: boolean;
  dashboardShowMattersList: boolean;
  medicalRecordsUploadEnabled: boolean;
}

const SETTINGS_STORAGE_KEY = 'storyboard-legal-settings';

class SettingsService {
  private settings: AppSettings;
  private uiFeaturesCache: UiFeatureFlags | null = null;
  private cacheExpiry: number = 60000; // 1 minute cache
  private lastCacheUpdate: number = 0;

  constructor() {
    this.settings = this.loadSettings();
    // Load UI features on initialization
    this.loadUiFeatures();
  }

  /**
   * Get current SharePoint Embedded settings
   */
  public getSharePointEmbeddedSettings(): SharePointEmbeddedSettings {
    return this.settings.sharePointEmbedded;
  }

  /**
   * Update SharePoint Embedded settings
   */
  public updateSharePointEmbeddedSettings(settings: Partial<SharePointEmbeddedSettings>): void {
    this.settings.sharePointEmbedded = {
      ...this.settings.sharePointEmbedded,
      ...settings
    };
    this.saveSettings();
  }

  /**
   * Get current role management settings
   */
  public getRoleManagementSettings(): RoleManagementSettings {
    return this.settings.roleManagement;
  }

  /**
   * Update role management settings
   */
  public updateRoleManagementSettings(settings: Partial<RoleManagementSettings>): void {
    this.settings.roleManagement = {
      ...this.settings.roleManagement,
      ...settings
    };
    this.saveSettings();
  }

  /**
   * Get all application settings
   */
  public getAllSettings(): AppSettings {
    return { ...this.settings };
  }

  /**
   * Get UI feature flags from API with authentication retry logic
   */
  public async getUiFeatures(): Promise<UiFeatureFlags> {
    // Return cached value if still valid
    if (this.uiFeaturesCache && this.isCacheValid()) {
      return this.uiFeaturesCache;
    }

    try {
      // Use authenticated API client with retry logic
      const response = await this.makeAuthenticatedRequest(() => 
        this.getUiFeaturesFromAPI()
      );
      
      this.uiFeaturesCache = response;
      this.lastCacheUpdate = Date.now();
      return response;
    } catch (error) {
      console.error('Failed to fetch UI features after retries:', error);
      
      // Return cached data if available (stale is better than broken)
      if (this.uiFeaturesCache) {
        console.warn('Using stale UI feature flags due to API error');
        return this.uiFeaturesCache;
      }
      
      // Only fall back to secure defaults as last resort
      console.warn('Using secure default UI features due to API error');
      return this.getDefaultUiFeatures();
    }
  }

  /**
   * Update UI feature flags with authentication
   */
  public async updateUiFeatures(features: Partial<UiFeatureFlags>): Promise<boolean> {
    try {
      const response = await this.makeAuthenticatedRequest(async () => {
        // Use a direct API call since apiClient doesn't have updateUiFeatures method
        const token = await getAccessToken(['https://graph.microsoft.com/.default']);
        const baseURL = this.getApiBaseUrl();
        
        const result = await fetch(`${baseURL}/settings/ui-features`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ features })
        });
        
        if (!result.ok) {
          throw new Error(`HTTP ${result.status}: ${result.statusText}`);
        }
        
        return await result.json();
      });
      
      if (response.success) {
        // Update cache with new values
        this.uiFeaturesCache = response.features;
        this.lastCacheUpdate = Date.now();
      }
      
      return response.success;
    } catch (error) {
      console.error('Failed to update UI features:', error);
      return false;
    }
  }

  /**
   * Load UI features in the background
   */
  private async loadUiFeatures(): Promise<void> {
    try {
      await this.getUiFeatures();
    } catch (error) {
      console.warn('Failed to load UI features on init:', error);
    }
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    return Date.now() - this.lastCacheUpdate < this.cacheExpiry;
  }

  /**
   * Get default UI features - SECURE BY DEFAULT
   * All features are disabled by default until explicitly enabled via API
   */
  private getDefaultUiFeatures(): UiFeatureFlags {
    return {
      dashboardShowReportCards: false,
      dashboardShowMattersList: false,
      medicalRecordsUploadEnabled: false
    };
  }

  /**
   * Get API base URL (similar to apiClient logic)
   */
  private getApiBaseUrl(): string {
    // Get API URL from environment variable first
    let baseURL = import.meta.env.VITE_API_BASE_URL;
    
    if (!baseURL && typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Local development - use standard API port
        baseURL = 'http://localhost:7071/api';
      } else if (hostname.includes('azurestaticapps.net')) {
        // Azure Static Web Apps
        baseURL = '/api';
      } else {
        // Custom domain
        baseURL = '/api';
      }
    }
    
    return baseURL || '/api';
  }

  /**
   * Make authenticated API request for UI features with retry logic
   */
  private async getUiFeaturesFromAPI(): Promise<UiFeatureFlags> {
    const token = await getAccessToken(['https://graph.microsoft.com/.default']);
    const baseURL = this.getApiBaseUrl();
    
    const response = await fetch(`${baseURL}/settings/ui-features`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Make authenticated request with retry logic for 401 errors
   * This handles the case where authentication might not be ready yet
   */
  private async makeAuthenticatedRequest<T>(
    requestFn: () => Promise<T>,
    maxRetries: number = 3,
    retryDelay: number = 1000
  ): Promise<T> {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error: any) {
        lastError = error;
        
        // Check if this is a 401 authentication error
        const isAuthError = (
          error.message?.includes('401') ||
          error.status === 401 ||
          error.message?.includes('Unauthorized')
        );
        
        if (isAuthError && attempt < maxRetries - 1) {
          console.log(`Authentication error on attempt ${attempt + 1}, retrying in ${retryDelay}ms...`);
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          
          // Exponential backoff for subsequent retries
          retryDelay *= 1.5;
          continue;
        }
        
        // If it's not an auth error, or we've exhausted retries, throw immediately
        throw error;
      }
    }
    
    // This shouldn't be reached, but just in case
    throw lastError || new Error('Request failed after all retries');
  }

  /**
   * Reset settings to defaults
   */
  public resetToDefaults(): void {
    this.settings = this.getDefaultSettings();
    this.saveSettings();
  }

  /**
   * Load settings from localStorage with fallback to defaults
   */
  private loadSettings(): AppSettings {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all required properties exist
        return {
          ...this.getDefaultSettings(),
          ...parsed,
          sharePointEmbedded: {
            ...this.getDefaultSettings().sharePointEmbedded,
            ...parsed.sharePointEmbedded
          },
          roleManagement: {
            ...this.getDefaultSettings().roleManagement,
            ...parsed.roleManagement
          }
        };
      }
    } catch (error) {
      console.warn('Failed to load settings from localStorage:', error);
    }
    
    return this.getDefaultSettings();
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }

  /**
   * Get default application settings
   */
  private getDefaultSettings(): AppSettings {
    return {
      sharePointEmbedded: {
        defaultGroupId: 'd97c448a-013b-4277-86e9-d4323dfee7ff',
        defaultGroupAlias: 'Legal Team Writers',
        defaultGroupEmail: 'Storyboard-Dev-Users@kizaninnovations.com'
      },
      roleManagement: {
        adminGroup: '',
        userGroup: ''
      },
      uiFeatures: this.getDefaultUiFeatures()
    };
  }
}

// Export singleton instance
export const settingsService = new SettingsService();
